import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  X,
  Trash2,
  Edit3,
  Truck,
  ArrowDownToLine,
  ArrowUpFromLine,
  CalendarDays,
  CalendarClock,
  Share2,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type LoadType = "inbound" | "outbound" | "event";

type ScheduledLoad = {
  id: number;
  type: LoadType;
  date: string;
  customer: string | null;
  driver: string | null;
  time_slot: string | null;
  destination: string | null;
  material: string | null;
  quantity: string | null;
  deal_name: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  notes: string | null;
};

type DayCell = {
  date: Date;
  dateStr: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  loads: ScheduledLoad[];
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function toDateStr(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function buildCalendarGrid(year: number, month: number): Date[] {
  const firstDay = new Date(year, month, 1);
  // Monday = 0, Sunday = 6
  let startOffset = firstDay.getDay() - 1;
  if (startOffset < 0) startOffset = 6;

  const start = new Date(year, month, 1 - startOffset);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    cells.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }
  return cells;
}

function formatDate(d: string) {
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

const EMPTY_LOAD: Omit<ScheduledLoad, "id"> = {
  type: "outbound",
  date: "",
  customer: "",
  driver: "",
  time_slot: "",
  destination: "",
  material: "",
  quantity: "",
  deal_name: "",
  contact_name: "",
  contact_phone: "",
  notes: "",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Calendar() {
  const { token, user } = useAuth();
  const today = new Date();

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [loads, setLoads] = useState<ScheduledLoad[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selected day panel
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Add/Edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLoad, setEditingLoad] = useState<ScheduledLoad | null>(null);
  const [form, setForm] = useState<Omit<ScheduledLoad, "id">>({ ...EMPTY_LOAD });
  const [formSaving, setFormSaving] = useState(false);

  const [shareCopied, setShareCopied] = useState(false);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
    [token],
  );

  // -----------------------------------------------------------------------
  // Data fetching
  // -----------------------------------------------------------------------

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const gridDates = buildCalendarGrid(year, month);
      const weekStart = toDateStr(gridDates[0]);
      const weekEnd = toDateStr(gridDates[gridDates.length - 1]);
      const res = await apiFetch(`/api/ops/calendar?weekStart=${weekStart}&weekEnd=${weekEnd}`, { headers });
      if (!res.ok) throw new Error(`Failed to load calendar (${res.status})`);
      const data = await res.json();
      setLoads(data.scheduled_loads ?? data.loads ?? data ?? []);
    } catch (err: any) {
      setError(err?.message || "Failed to load calendar data");
    } finally {
      setLoading(false);
    }
  }, [year, month, headers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // -----------------------------------------------------------------------
  // Calendar grid
  // -----------------------------------------------------------------------

  const grid = useMemo(() => {
    const dates = buildCalendarGrid(year, month);
    const todayStr = toDateStr(today);

    const loadsByDate = new Map<string, ScheduledLoad[]>();
    for (const load of loads) {
      const key = load.date.slice(0, 10);
      const existing = loadsByDate.get(key);
      if (existing) existing.push(load);
      else loadsByDate.set(key, [load]);
    }

    return dates.map((d): DayCell => {
      const dateStr = toDateStr(d);
      return {
        date: d,
        dateStr,
        isCurrentMonth: d.getMonth() === month,
        isToday: dateStr === todayStr,
        loads: loadsByDate.get(dateStr) || [],
      };
    });
  }, [year, month, loads]);

  // -----------------------------------------------------------------------
  // Month stats
  // -----------------------------------------------------------------------

  const monthLoads = useMemo(() => {
    return loads.filter((l) => {
      const d = new Date(l.date.slice(0, 10) + "T00:00:00");
      return d.getMonth() === month && d.getFullYear() === year;
    });
  }, [loads, month, year]);

  const totalLoads = monthLoads.length;
  const inboundCount = monthLoads.filter((l) => l.type === "inbound").length;
  const outboundCount = monthLoads.filter((l) => l.type === "outbound").length;
  const eventCount = monthLoads.filter((l) => l.type === "event").length;

  // -----------------------------------------------------------------------
  // Navigation
  // -----------------------------------------------------------------------

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(year - 1); }
    else setMonth(month - 1);
  };

  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(year + 1); }
    else setMonth(month + 1);
  };

  const goToday = () => {
    setYear(today.getFullYear());
    setMonth(today.getMonth());
  };

  // -----------------------------------------------------------------------
  // Selected day loads
  // -----------------------------------------------------------------------

  const selectedLoads = useMemo(() => {
    if (!selectedDate) return [];
    return loads.filter((l) => l.date.slice(0, 10) === selectedDate);
  }, [loads, selectedDate]);

  // -----------------------------------------------------------------------
  // Add / Edit / Delete
  // -----------------------------------------------------------------------

  const openAddModal = (date?: string) => {
    setEditingLoad(null);
    setForm({ ...EMPTY_LOAD, date: date || toDateStr(today) });
    setModalOpen(true);
  };

  const openEditModal = (load: ScheduledLoad) => {
    setEditingLoad(load);
    setForm({
      type: load.type,
      date: load.date.slice(0, 10),
      customer: load.customer || "",
      driver: load.driver || "",
      time_slot: load.time_slot || "",
      destination: load.destination || "",
      material: load.material || "",
      quantity: load.quantity || "",
      deal_name: load.deal_name || "",
      contact_name: load.contact_name || "",
      contact_phone: load.contact_phone || "",
      notes: load.notes || "",
    });
    setModalOpen(true);
  };

  const saveLoad = async () => {
    setFormSaving(true);
    setError(null);
    try {
      const body = {
        date: form.date,
        routeType: form.type,
        customer: form.customer || "",
        driver: form.driver || null,
        timeSlot: form.time_slot || null,
        destination: form.destination || "",
        material: form.material || "",
        quantity: form.quantity || null,
        deal: form.deal_name || null,
        contactName: form.contact_name || null,
        contactPhone: form.contact_phone || null,
        notes: form.notes || null,
      };

      const url = editingLoad ? `/api/ops/calendar/${editingLoad.id}` : "/api/ops/calendar";
      const method = editingLoad ? "PATCH" : "POST";

      const res = await apiFetch(url, { method, headers, body: JSON.stringify(body) });
      if (!res.ok) throw new Error(`Failed to ${editingLoad ? "update" : "create"} load`);

      setModalOpen(false);
      await loadData();
    } catch (err: any) {
      setError(err?.message || "Failed to save load");
    } finally {
      setFormSaving(false);
    }
  };

  const deleteLoad = async (id: number) => {
    if (!window.confirm("Delete this scheduled load?")) return;
    try {
      const res = await apiFetch(`/api/ops/calendar/${id}`, { method: "DELETE", headers });
      if (!res.ok) throw new Error("Failed to delete load");
      await loadData();
    } catch (err: any) {
      setError(err?.message || "Failed to delete load");
    }
  };

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Operations Calendar</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Schedule loads and events
          </p>
        </div>
        <button
          onClick={() => openAddModal()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#264027] hover:bg-[#1a2e1b] text-white rounded-lg text-sm font-semibold transition-colors"
        >
          <Plus size={16} /> Add Entry
        </button>
      </div>

      {/* Month stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-semibold uppercase tracking-wide mb-2">
            <Truck size={14} /> Total
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalLoads}</p>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2 text-blue-500 dark:text-blue-400 text-xs font-semibold uppercase tracking-wide mb-2">
            <ArrowDownToLine size={14} /> Inbound
          </div>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{inboundCount}</p>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-xs font-semibold uppercase tracking-wide mb-2">
            <ArrowUpFromLine size={14} /> Outbound
          </div>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">{outboundCount}</p>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400 text-xs font-semibold uppercase tracking-wide mb-2">
            <CalendarClock size={14} /> Events
          </div>
          <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{eventCount}</p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-700 dark:text-red-400">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      <div className="flex flex-col gap-6">
        {/* Calendar grid */}
        <div className="flex-1">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button onClick={prevMonth} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors">
                <ChevronLeft size={18} />
              </button>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white min-w-[180px] text-center">
                {MONTHS[month]} {year}
              </h2>
              <button onClick={nextMonth} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors">
                <ChevronRight size={18} />
              </button>
            </div>
            <button
              onClick={goToday}
              className="px-3 py-1.5 text-xs font-semibold bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg transition-colors"
            >
              Today
            </button>
          </div>

          {loading ? (
            <div className="text-center py-20 text-gray-400">Loading calendar...</div>
          ) : (
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden shadow-sm">
              {/* Weekday headers */}
              <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50">
                {WEEKDAYS.map((d) => (
                  <div key={d} className="text-center text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider py-3">
                    {d}
                  </div>
                ))}
              </div>

              {/* Day cells */}
              <div className="grid grid-cols-7">
                {grid.map((cell) => {
                  const hasInbound = cell.loads.some((l) => l.type === "inbound");
                  const hasOutbound = cell.loads.some((l) => l.type === "outbound");
                  const hasEvent = cell.loads.some((l) => l.type === "event");
                  const isSelected = selectedDate === cell.dateStr;

                  return (
                    <button
                      key={cell.dateStr}
                      onClick={() => setSelectedDate(isSelected ? null : cell.dateStr)}
                      className={`
                        relative min-h-[88px] p-2 border-b border-r border-gray-100 dark:border-gray-800/60 text-left transition-colors
                        ${cell.isCurrentMonth ? "bg-white dark:bg-gray-900" : "bg-gray-50/60 dark:bg-gray-950/60"}
                        ${isSelected ? "ring-2 ring-[#264027] ring-inset z-10" : ""}
                        hover:bg-gray-50 dark:hover:bg-gray-800/60
                      `}
                    >
                      <span
                        className={`
                          text-sm font-medium
                          ${cell.isToday ? "w-7 h-7 rounded-full bg-[#264027] text-white flex items-center justify-center" : ""}
                          ${cell.isCurrentMonth ? "text-gray-900 dark:text-white" : "text-gray-400 dark:text-gray-600"}
                        `}
                      >
                        {cell.date.getDate()}
                      </span>
                      {/* Dots */}
                      {(hasInbound || hasOutbound || hasEvent) && (
                        <div className="flex gap-1 mt-1">
                          {hasOutbound && <span className="w-2 h-2 rounded-full bg-green-500" title="Outbound" />}
                          {hasInbound && <span className="w-2 h-2 rounded-full bg-blue-500" title="Inbound" />}
                          {hasEvent && <span className="w-2 h-2 rounded-full bg-purple-500" title="Event" />}
                          {cell.loads.length > 1 && (
                            <span className="text-[10px] text-gray-400 font-medium ml-0.5">{cell.loads.length}</span>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Day sidebar — slides in from right */}
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-200 ${
          selectedDate ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!selectedDate}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedDate(null)} />
        <aside
          className={`absolute top-0 right-0 h-full w-full sm:w-[420px] bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 shadow-2xl flex flex-col overflow-hidden transform transition-transform duration-200 ease-out sm:rounded-l-2xl ${
            selectedDate ? "translate-x-0" : "translate-x-full"
          }`}
        >
        {selectedDate && (
          <div id="calendar-day-panel" className="flex flex-col h-full">
            {/* Sticky header */}
            <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-gray-900/95 backdrop-blur">
              <div className="min-w-0">
                <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">{formatDate(selectedDate)}</h3>
                <p className="text-xs text-gray-400 mt-0.5">{selectedLoads.length} {selectedLoads.length === 1 ? "entry" : "entries"}</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => {
                    const orgId = user?.organization_id || 1;
                    const url = `${window.location.origin}/share/calendar/${orgId}/${selectedDate}`;
                    if (navigator.share) {
                      navigator.share({ title: `Schedule for ${formatDate(selectedDate)}`, url }).catch(() => {});
                    } else {
                      navigator.clipboard.writeText(url);
                      setShareCopied(true);
                      setTimeout(() => setShareCopied(false), 2000);
                    }
                  }}
                  title="Copy share link"
                  className="h-9 px-3 inline-flex items-center gap-1.5 rounded-lg text-xs font-semibold bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors"
                >
                  <Share2 size={14} /> {shareCopied ? "Copied!" : "Share"}
                </button>
                <button
                  onClick={() => openAddModal(selectedDate)}
                  title="Add entry"
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#264027] hover:bg-[#1a2e1b] text-white transition-colors"
                >
                  <Plus size={16} />
                </button>
                <button
                  onClick={() => setSelectedDate(null)}
                  title="Close"
                  className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
            {selectedLoads.length === 0 ? (
              <div className="text-center py-12">
                <CalendarDays size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-sm text-gray-400 mb-3">Nothing scheduled</p>
                <button
                  onClick={() => openAddModal(selectedDate)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#264027] hover:bg-[#1a2e1b] text-white transition-colors"
                >
                  <Plus size={12} /> Add entry
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedLoads.map((load) => (
                  <div
                    key={load.id}
                    onClick={() => openEditModal(load)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openEditModal(load);
                      }
                    }}
                    className={`p-3 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${
                      load.type === "inbound"
                        ? "border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/10"
                        : load.type === "event"
                        ? "border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/10"
                        : "border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-bold uppercase tracking-wide ${
                        load.type === "inbound"
                          ? "text-blue-600 dark:text-blue-400"
                          : load.type === "event"
                          ? "text-purple-600 dark:text-purple-400"
                          : "text-green-600 dark:text-green-400"
                      }`}>
                        {load.type === "inbound" ? "Inbound" : load.type === "event" ? "Event" : "Outbound"}
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); openEditModal(load); }}
                          className="w-6 h-6 flex items-center justify-center rounded hover:bg-white/50 dark:hover:bg-gray-800/50 text-gray-400"
                        >
                          <Edit3 size={12} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteLoad(load.id); }}
                          className="w-6 h-6 flex items-center justify-center rounded hover:bg-white/50 dark:hover:bg-gray-800/50 text-red-400"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                    {load.customer && <p className="text-sm font-semibold text-gray-900 dark:text-white">{load.customer}</p>}
                    {load.deal_name && <p className="text-xs text-gray-500 dark:text-gray-400">{load.deal_name}</p>}
                    {load.material && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Material: {load.material}</p>}
                    {load.driver && <p className="text-xs text-gray-500 dark:text-gray-400">Driver: {load.driver}</p>}
                    {load.time_slot && <p className="text-xs text-gray-500 dark:text-gray-400">Time: {load.time_slot}</p>}
                    {load.destination && <p className="text-xs text-gray-500 dark:text-gray-400">{load.type === "event" ? "Location" : "To"}: {load.destination}</p>}
                    {load.quantity && <p className="text-xs text-gray-500 dark:text-gray-400">Qty: {load.quantity}</p>}
                    {load.contact_name && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Contact: {load.contact_name}{load.contact_phone ? ` (${load.contact_phone})` : ""}
                      </p>
                    )}
                    {load.notes && <p className="text-xs text-gray-400 mt-1 italic">{load.notes}</p>}
                  </div>
                ))}
              </div>
            )}
            </div>
          </div>
        )}
        </aside>
      </div>

      {/* Add/Edit modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setModalOpen(false)} />
          <div className="relative w-full max-w-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl overflow-y-auto max-h-[90vh]">
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  {editingLoad ? "Edit Entry" : "Add Entry"}
                </h2>
                <button
                  onClick={() => setModalOpen(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Type */}
              <div className="flex gap-2">
                {(["outbound", "inbound", "event"] as LoadType[]).map((t) => (
                  <button
                    key={t}
                    onClick={() => updateField("type", t)}
                    className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
                      form.type === t
                        ? t === "inbound"
                          ? "bg-blue-600 text-white"
                          : t === "event"
                          ? "bg-purple-600 text-white"
                          : "bg-green-600 text-white"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    {t === "inbound" ? "Inbound" : t === "event" ? "Event" : "Outbound"}
                  </button>
                ))}
              </div>

              {/* Date */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Date</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => updateField("date", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    {form.type === "event" ? "Title" : "Customer"}
                  </label>
                  <input
                    value={form.customer || ""}
                    onChange={(e) => updateField("customer", e.target.value)}
                    placeholder={form.type === "event" ? "e.g. Strategic Operations Meeting" : ""}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    {form.type === "event" ? "Time" : "Driver"}
                  </label>
                  <input
                    value={form.type === "event" ? (form.time_slot || "") : (form.driver || "")}
                    onChange={(e) => updateField(form.type === "event" ? "time_slot" : "driver", e.target.value)}
                    placeholder={form.type === "event" ? "e.g. 12:00 PM" : ""}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                  />
                </div>
              </div>

              {form.type !== "event" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Time Slot</label>
                    <input
                      value={form.time_slot || ""}
                      onChange={(e) => updateField("time_slot", e.target.value)}
                      placeholder="e.g. 8:00 AM"
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Destination</label>
                    <input
                      value={form.destination || ""}
                      onChange={(e) => updateField("destination", e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                    />
                  </div>
                </div>
              )}

              {form.type === "event" && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Location</label>
                  <input
                    value={form.destination || ""}
                    onChange={(e) => updateField("destination", e.target.value)}
                    placeholder="e.g. Restaurante Wickenburg"
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                  />
                </div>
              )}

              {form.type !== "event" && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Material</label>
                      <input
                        value={form.material || ""}
                        onChange={(e) => updateField("material", e.target.value)}
                        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Quantity</label>
                      <input
                        value={form.quantity || ""}
                        onChange={(e) => updateField("quantity", e.target.value)}
                        placeholder="e.g. 20 tons"
                        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Deal Name</label>
                    <input
                      value={form.deal_name || ""}
                      onChange={(e) => updateField("deal_name", e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                    />
                  </div>
                </>
              )}

              {form.type !== "event" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Contact Name</label>
                    <input
                      value={form.contact_name || ""}
                      onChange={(e) => updateField("contact_name", e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Contact Phone</label>
                    <input
                      value={form.contact_phone || ""}
                      onChange={(e) => updateField("contact_phone", e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Notes</label>
                <textarea
                  rows={2}
                  value={form.notes || ""}
                  onChange={(e) => updateField("notes", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm resize-none"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={saveLoad}
                  disabled={formSaving || !form.date}
                  className="flex-1 px-4 py-2.5 bg-[#264027] hover:bg-[#1a2e1b] disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors"
                >
                  {formSaving ? "Saving..." : editingLoad ? "Update Load" : "Add Load"}
                </button>
                <button
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg text-sm font-semibold transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
